import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";

import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import CircularProgress from "@mui/material/CircularProgress";
import Avatar from "@mui/material/Avatar";
import { LoadingButton } from "@mui/lab";

import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import DoneOutlinedIcon from "@mui/icons-material/DoneOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import FileOutlined from "../../assets/FileOutlined.svg";

import { RootContext } from "../../context/RootContext";
import { LoansContext } from "../../context/LoansContext";
import { useUserDisplayName } from "../../hooks/useUserDisplayName";
import {
  WaiverStatus,
  WaiverServiceRecord,
  asMitigationFactorArray,
  getWaiverStatus,
  isWaiverDecided,
  getWaiverMitigationDescriptions,
  getWaiverDocuments,
  getLatestWaiverResponse,
  resetWaiverStatus,
  downloadWaiverDocument,
} from "./utils";
import WaiverActionForm from "./WaiverActionForm";

const getInitials = (fullName: string | undefined) => {
  const parts = (fullName ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
};

// `cardColor` drives both the card's border and its header background — for
// decided waivers those are the same flat color; Pending keeps the original
// translucent-yellow border paired with a slightly different solid header
// tint, so it gets its own explicit `cardBorder`.
const STATUS_CONFIG: Record<
  string,
  {
    labelKey: string;
    color: string;
    cardBorder: string;
    cardBackground: string;
    icon?: typeof DoneOutlinedIcon;
  }
> = {
  [WaiverStatus.PENDING]: {
    labelKey: "pendingReview",
    color: "#F5A623",
    cardBorder: "rgba(252, 203, 29, 0.30)",
    cardBackground: "#FFF1DB",
  },
  [WaiverStatus.APPROVED]: {
    labelKey: "testWaiverApproved",
    color: "#2d6c45",
    cardBorder: "#EDF7F1",
    cardBackground: "#EDF7F1",
    icon: DoneOutlinedIcon,
  },
  [WaiverStatus.REJECTED]: {
    labelKey: "testWaiverRejected",
    color: "#b30c00",
    cardBorder: "#fad0cd",
    cardBackground: "#fad0cd",
    icon: ClearOutlinedIcon,
  },
  [WaiverStatus.APPROVED_CONDITIONALLY]: {
    labelKey: "testWaiverApprovedConditionally",
    color: "#e38f0e",
    cardBorder: "#FFF1DB",
    cardBackground: "#FFF1DB",
    icon: ReportProblemOutlinedIcon,
  },
};

export default function WaiverSummary({
  waiver,
  canActOnWaiver = false,
}: {
  waiver: WaiverServiceRecord;
  canActOnWaiver?: boolean;
}) {
  const { t } = useTranslation();
  const { dynamicConfig } = useContext(RootContext) ?? {};
  const { setLoanDetails } = useContext(LoansContext) ?? {};
  const [downloadingPath, setDownloadingPath] = useState("");
  const [isActionFormOpen, setIsActionFormOpen] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [resetError, setResetError] = useState("");

  const status = getWaiverStatus(waiver);
  const statusConfig = STATUS_CONFIG[status] ?? STATUS_CONFIG[WaiverStatus.PENDING];
  const isDecided = isWaiverDecided(status);
  const latestResponse = getLatestWaiverResponse(waiver);
  const currentActionAudit = latestResponse?.responseBy
    ? { by: latestResponse.responseBy, on: latestResponse.responseDate }
    : null;
  const docs = getWaiverDocuments(waiver);

  // createdBy/responseBy are Keycloak userIds on the raw waiver-service
  // record — resolve them to display names rather than showing the id.
  const createdByName = useUserDisplayName(waiver.createdBy);
  const actionByName = useUserDisplayName(currentActionAudit?.by);

  const downloadFile = async (filePath: string) => {
    setDownloadingPath(filePath);
    try {
      const realmHeaders = { "kc-realm": dynamicConfig?.ViteAppKcRealm };
      const url = await downloadWaiverDocument(waiver.waiverId ?? "", filePath, realmHeaders);
      // A programmatically-clicked <a> triggers the browser's native download
      // handling directly, unlike window.open(url) — which is unreliable for
      // presigned GCS URLs (typically served as a Content-Disposition:
      // attachment, so a new tab has nothing to render and just closes
      // itself) and gets popup-blocked outright if opened after this await.
      if (url) {
        const link = document.createElement("a");
        link.href = url;
        link.target = "_blank";
        link.rel = "noopener noreferrer";
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (err) {
      console.error("Something went wrong while downloading file. ERROR:", err);
    } finally {
      setDownloadingPath("");
    }
  };

  const handleResetWaiver = async () => {
    // Records the reset as a real "Re-Requested" response via the waiver
    // service — the same endpoint approve/reject/conditional-approve use —
    // so it persists across a refresh instead of being a local-only change.
    setIsResetting(true);
    setResetError("");
    try {
      await resetWaiverStatus(setLoanDetails, waiver);
    } catch (err) {
      setResetError(t("waiverActionFailed"));
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <Box
      data-testid="waiver-summary"
      sx={{
        marginTop: "24px",
        borderRadius: "4px 4px 0 0",
        borderTop: `1px solid ${statusConfig.cardBorder}`,
        borderRight: `1px solid ${statusConfig.cardBorder}`,
        borderLeft: `1px solid ${statusConfig.cardBorder}`,
        background: "#fff",
      }}
    >
      <Grid container>
        <Grid
          item
          xs={12}
          style={{
            display: "flex",
            padding: "10px 16px",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: "16px",
            alignSelf: "stretch",
            borderRadius: "4px 4px 0 0",
            borderTop: `1px solid ${statusConfig.cardBorder}`,
            borderRight: `1px solid ${statusConfig.cardBorder}`,
            borderLeft: `1px solid ${statusConfig.cardBorder}`,
            background: statusConfig.cardBackground,
          }}
        >
          <div
            style={{
              display: "flex",
              width: "100%",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <h3
              style={{
                color: "#000",
                fontFamily: "Poppins",
                fontSize: "16px",
                fontStyle: "normal",
                fontWeight: 500,
                lineHeight: "normal",
                margin: 0,
                padding: 0,
              }}
            >
              {t("testWaiverRequestedHeader")}
            </h3>
            <Chip
              size="small"
              label={t(statusConfig.labelKey)}
              icon={
                statusConfig.icon ? (
                  <statusConfig.icon fontSize="small" style={{ color: "#fff" }} />
                ) : undefined
              }
              style={{ background: statusConfig.color, color: "#fff" }}
            />
          </div>
          {isDecided && currentActionAudit?.by ? (
            <div
              data-testid="waiver-decided-by"
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                color: "#383847",
              }}
            >
              {t(statusConfig.labelKey)} {t("byLabel")}: {actionByName}{" "}
              &bull;{" "}
              {moment(currentActionAudit.on).format("MMM DD, YYYY, hh:mm A")}
            </div>
          ) : null}
        </Grid>
      </Grid>

      <Grid container style={{ padding: "16px" }}>
        <Grid item xs={12}>
          <span
            style={{
              fontSize: "12px",
              lineHeight: "20px",
              letterSpacing: "0.4px",
              fontWeight: 700,
              display: "block",
            }}
          >
            {t("mitigationFactor")}
          </span>
          <div style={{ fontSize: "14px", fontWeight: 500 }}>
            {getWaiverMitigationDescriptions(waiver).join(", ")}
          </div>
        </Grid>

        {waiver.description ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            <span
              style={{
                fontSize: "12px",
                lineHeight: "20px",
                letterSpacing: "0.4px",
                fontWeight: 700,
                display: "block",
              }}
            >
              {t("waiverDescription")}
            </span>
            <div style={{ fontSize: "14px" }}>{waiver.description}</div>
          </Grid>
        ) : null}

        {docs.length > 0 ? (
          <Grid
            item
            xs={12}
            style={{
              paddingTop: "16px",
              display: "flex",
              flexDirection: "column",
              gap: "8px",
            }}
          >
            {docs.map((doc, index) => (
              <div
                key={`${doc.fileName}-${index}`}
                data-testid="waiver-summary-document-item"
                style={{
                  borderRadius: "4px",
                  border: "1px solid #DBE2FF",
                  background: "#F8F9FD",
                  display: "flex",
                  padding: "8px 10px",
                  alignItems: "center",
                  gap: "8px",
                  alignSelf: "stretch",
                  justifyContent: "space-between",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", minWidth: 0, flex: 1 }}>
                  <img
                    src={FileOutlined}
                    alt="file-icon"
                    style={{ width: "16px", height: "16px", marginRight: "8px", flexShrink: 0 }}
                  />
                  <span
                    title={doc.fileName}
                    style={{
                      color: "#383847",
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "16px",
                      letterSpacing: "0.064px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {doc.fileName}
                  </span>
                </div>
                <IconButton
                  size="small"
                  aria-label="download"
                  onClick={() => downloadFile(doc.filePath)}
                  disabled={downloadingPath === doc.filePath}
                  data-testid="waiver-summary-download-button"
                >
                  {downloadingPath === doc.filePath ? (
                    <CircularProgress size={16} />
                  ) : (
                    <FileDownloadOutlinedIcon fontSize="small" style={{ color: "#383847" }}/>
                  )}
                </IconButton>
              </div>
            ))}
          </Grid>
        ) : null}

        <Grid
          item
          xs={12}
          style={{
            paddingTop: "16px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <Avatar
            sx={{
              width: 24,
              height: 24,
              fontSize: "12px",
              borderRadius: "12px",
              background: "#655DBB",
            }}
          >
            {getInitials(createdByName)}
          </Avatar>
          <span
            style={{
              color: "#383847",
              fontFamily: "Poppins",
              fontSize: "12px",
              fontStyle: "normal",
              fontWeight: 400,
              lineHeight: "normal",
              letterSpacing: "0.4px",
            }}
          >
            {createdByName} &bull;{" "}
            {moment(waiver.createdOn).format("MMM DD, YYYY, hh:mm A")}
          </span>
        </Grid>

        {latestResponse && isDecided ? (
          <Grid
            item
            xs={12}
            style={{
              paddingTop: "16px",
              borderTop: "1px solid #DBE2FF",
              marginTop: "16px",
            }}
            data-testid="waiver-underwriter-action"
          >
            <div
              style={{
                borderRadius: "4px",
                border: "1px solid #DBE2FF",
                background: "#F8F9FD",
                padding: "12px",
              }}
            >
              <span
                style={{
                  fontSize: "12px",
                  lineHeight: "20px",
                  letterSpacing: "0.4px",
                  fontWeight: 700,
                  display: "block",
                }}
              >
                {t("compensatingFactor")}
              </span>
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "8px",
                }}
              >
                {asMitigationFactorArray(
                  latestResponse?.compensatingFactors,
                ).map((factor) => (
                  <Chip
                    key={factor}
                    size="small"
                    label={factor}
                    data-testid="compensating-factor-chip"
                    style={{
                      borderRadius: "40px",
                      background: "#40B4B4",
                      display: "flex",
                      padding: "2px 8px",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: "10px",
                      color: "#fff",
                      height: "auto",
                    }}
                  />
                ))}
              </div>
              {latestResponse?.comments ? (
                <>
                  <span
                    style={{
                      fontSize: "12px",
                      lineHeight: "20px",
                      letterSpacing: "0.4px",
                      fontWeight: 700,
                      display: "block",
                      marginTop: "12px",
                    }}
                  >
                    {t("optionalComments")}
                  </span>
                  <div style={{ fontSize: "14px" }}>
                    {latestResponse.comments}
                  </div>
                </>
              ) : null}
              {currentActionAudit?.by ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginTop: "16px",
                  }}
                >
                  <Avatar
                    sx={{
                      width: 24,
                      height: 24,
                      fontSize: "12px",
                      borderRadius: "12px",
                      background: "#655DBB",
                    }}
                  >
                    {getInitials(actionByName)}
                  </Avatar>
                  <span
                    style={{
                      color: "#383847",
                      fontFamily: "Poppins",
                      fontSize: "12px",
                      fontStyle: "normal",
                      fontWeight: 400,
                      lineHeight: "normal",
                      letterSpacing: "0.4px",
                    }}
                  >
                    {actionByName} &bull;{" "}
                    {moment(currentActionAudit.on).format(
                      "MMM DD, YYYY, hh:mm A",
                    )}
                  </span>
                </div>
              ) : null}
            </div>
          </Grid>
        ) : null}

        {canActOnWaiver && !isActionFormOpen ? (
          <Grid item xs={12} style={{ paddingTop: "16px" }}>
            {isDecided ? (
              <LoadingButton
                variant="outlined"
                size="small"
                loading={isResetting}
                onClick={handleResetWaiver}
                data-testid="reset-waiver-button"
              >
                {t("resetWaiverText")}
              </LoadingButton>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<DoneOutlinedIcon fontSize="small" />}
                onClick={() => setIsActionFormOpen(true)}
                data-testid="act-on-waiver-button"
              >
                {t("actOnWaiver")}
              </Button>
            )}
            {resetError ? (
              <div style={{ color: "#ef4444", fontSize: "12px", marginTop: "8px" }}>
                {resetError}
              </div>
            ) : null}
          </Grid>
        ) : null}
      </Grid>
      {latestResponse && isDecided ? (
        <hr style={{ border: "1px solid #DBE2FF", margin: "0px 16px", background: "#DBE2FF" }} />
      ) : null}
      {canActOnWaiver && isActionFormOpen ? (
        <WaiverActionForm
          waiver={waiver}
          onClose={() => setIsActionFormOpen(false)}
        />
      ) : null}
    </Box>
  );
}
